import { Globe, Question, Lightbulb, Gear, ArrowRight, Sparkle, TrendUp, TrendDown, Bank, Buildings, CurrencyCircleDollar, Factory, Users, ChartLine, Briefcase, Percent } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

function Section({ icon: Icon, title, subtitle, tone, children, testid }) {
  const tones = {
    amber: "border-amber-500/30 bg-amber-500/5",
    emerald: "border-emerald-500/25 bg-emerald-500/5",
    blue: "border-blue-500/25 bg-blue-500/5",
  };
  const iconTones = {
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  };
  return (
    <section className={`rounded-sm border ${tones[tone]} p-5 sm:p-6`} data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-sm border flex items-center justify-center shrink-0 ${iconTones[tone]}`}>
          <Icon size={20} weight="bold" />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-zinc-50">{title}</h2>
          {subtitle && <p className="text-[11px] sm:text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-3 text-sm text-zinc-300 leading-relaxed">{children}</div>
    </section>
  );
}

function Pill({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2.5 p-3 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
      {Icon && <Icon size={14} weight="bold" className="text-amber-400 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs font-medium text-zinc-100 mb-0.5">{title}</p>
        <p className="text-[11px] text-zinc-400 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

export default function Macroeconomics() {
  return (
    <div className="p-5 sm:p-6 space-y-5 max-w-[1100px] overflow-x-hidden" data-testid="macro-page">
      {/* Hero */}
      <div className="rounded-sm border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-zinc-900/30 to-zinc-950 p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-2">
          <Globe size={22} weight="bold" className="text-amber-400" />
          <Badge className="text-[9px] px-1.5 py-0 h-4 border-0 rounded-sm bg-amber-500/15 text-amber-300">BIG PICTURE</Badge>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight">Macroeconomics</h1>
        <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-2xl">
          The study of the <span className="text-amber-300 font-medium">whole economy</span> — whole countries, whole industries, the global system.
          This is why interest rates, inflation, jobs, and GDP matter to your portfolio.
        </p>
      </div>

      {/* WHAT */}
      <Section icon={Question} title="What is macroeconomics?" subtitle="Zoomed-out view of money and markets" tone="amber" testid="macro-what">
        <p>
          Macroeconomics looks at <span className="text-amber-300 font-medium">entire economies</span> — how much a country produces, how many people have jobs,
          how fast prices are rising, how much the central bank is lending, and how governments tax and spend.
        </p>
        <p>Think weather forecast, but for money. It tells you whether the economy's climate is warming, cooling, stormy, or sunny.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
          <Pill icon={ChartLine} title="Growth" body="Is the economy getting bigger or smaller? GDP measures this." />
          <Pill icon={Percent} title="Inflation" body="Are prices going up or down? CPI measures this." />
          <Pill icon={Users} title="Jobs" body="Are people working or unemployed? Unemployment rate measures this." />
        </div>
      </Section>

      {/* WHY */}
      <Section icon={Lightbulb} title="Why should you care?" subtitle="Macro moves every asset you own" tone="emerald" testid="macro-why">
        <p>Every crypto coin, every stock, every savings account is affected by macro forces — often more than the specific company or project itself.</p>
        <ol className="space-y-2 pl-1">
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">01</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Interest rates set the tide.</strong> When the Fed raises rates, borrowing gets expensive — stocks &amp; crypto usually fall. When they cut, money gets cheap — risk assets usually rally.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">02</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Inflation eats your savings.</strong> 3% inflation = your $10,000 savings loses $300 of buying power a year even if the balance doesn't change.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">03</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Jobs = spending.</strong> High unemployment → less spending → companies earn less → stocks drop. Low unemployment → more spending → boom (but also more inflation).</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">04</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Money supply moves Bitcoin.</strong> When central banks print more dollars (M2 rises), Bitcoin &amp; gold tend to rise — they're seen as hedges against money-printing.</span>
          </li>
        </ol>
        <div className="mt-4 p-3 rounded-sm border border-emerald-500/20 bg-emerald-500/5">
          <p className="text-xs text-zinc-300 leading-relaxed">
            <Sparkle size={12} weight="fill" className="inline text-emerald-400 mr-1" />
            <strong className="text-emerald-300">Takeaway:</strong> stock-picking skill barely matters in a bad macro environment. Learn to read the macro first —
            then the specific assets second. The Golden Nest's <span className="text-amber-300">Economic Data</span> tab tracks these live.
          </p>
        </div>
      </Section>

      {/* HOW */}
      <Section icon={Gear} title="The 6 macro indicators that matter most" subtitle="Everything else is noise until you know these" tone="blue" testid="macro-how">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <ChartLine size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">GDP</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              <strong className="text-zinc-200">Gross Domestic Product</strong> — total value of everything a country makes in a year. Rising GDP = growing economy. Two negative quarters in a row = recession.
            </p>
          </div>
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <Percent size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">CPI (Inflation)</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              <strong className="text-zinc-200">Consumer Price Index</strong> — average change in prices for a basket of everyday goods. 2% is healthy, 5%+ is painful, above 8% is crisis territory.
            </p>
          </div>
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <Bank size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Interest Rate (Fed Funds)</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              The rate the central bank charges banks overnight. It trickles down into mortgages, credit cards, and — crucially — how expensive it is for businesses to borrow.
            </p>
          </div>
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <Users size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Unemployment</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              % of people looking for work who can't find it. Below 4% = very tight labor market. Above 6% = weakening economy.
            </p>
          </div>
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <CurrencyCircleDollar size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">M2 Money Supply</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Total cash + easy-to-access money in the system. Rising M2 = more dollars chasing the same stuff = usually inflationary and good for hard assets like BTC &amp; gold.
            </p>
          </div>
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <TrendUp size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">10-Year Treasury Yield</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Interest rate on 10-year US government bonds. The benchmark everything is priced against. Rising yields = headwind for stocks &amp; crypto. Falling yields = tailwind.
            </p>
          </div>
        </div>

        <div className="mt-4 p-4 rounded-sm border border-blue-500/20 bg-blue-500/5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={14} weight="fill" className="text-blue-400" />
            <p className="text-xs font-medium text-blue-200">The macro cheat sheet</p>
          </div>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <strong className="text-zinc-100">Rates up, inflation high → risk assets struggle.</strong><br/>
            <strong className="text-zinc-100">Rates down, M2 growing → risk assets (stocks, crypto) tend to fly.</strong><br/>
            That's 80% of the game. Check these 6 numbers once a week and you'll understand most market moves.
          </p>
        </div>
      </Section>

      {/* Next steps */}
      <div className="rounded-sm border border-amber-500/25 bg-zinc-900/40 p-5 sm:p-6" data-testid="macro-next-steps">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRight size={16} weight="bold" className="text-amber-400" />
          <h3 className="font-heading text-base font-bold text-zinc-100">Where to go from here</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Pill icon={ChartLine} title="Economic Data tab" body="Live CPI, GDP, unemployment, M2, 10Y yield, oil &amp; gas — track what moves markets." />
          <Pill icon={Buildings} title="Microeconomics tab" body="Zoom into individual companies, supply &amp; demand, and consumer behavior — the other half of the story." />
        </div>
      </div>
    </div>
  );
}
